import { useEffect, useRef } from "react";
import type { Message, PendingApproval } from "../../shared/types";
import type { PlaybackState } from "../hooks/useVoiceOutput";
import type { ConversationUIState } from "../types";
import {
  RECAPTCHA_PARAM_ACTION,
  RECAPTCHA_PARAM_SITE_KEY,
  RECAPTCHA_TYPE,
} from "../hooks/recaptchaConstants";
import { StatusMessage } from "./StatusMessage";
import { MarkdownMessage } from "./MarkdownMessage";
import { ErrorMessage } from "./ErrorMessage";
import { ToolApprovalCard } from "./ToolApprovalCard";
import { ScfToolApprovalCard } from "./ScfToolApprovalCard";
import { COLLECT_IMAGES_TOOL_NAME } from "../../shared/constants";
import { ImageCollectionCard } from "./ImageCollectionCard";
import { trackWidgetEvent } from "../../telemetry/events";

function SpeakerWaveIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 28 24"
      fill="none"
      aria-hidden="true"
      width="24"
      height="24"
      className="block shrink-0"
    >
      <g clipPath="url(#speaker-wave-icon-clip)">
        <path
          d="M10.75 10.2501V13.7501H13.0833L16 16.6668V7.33344L13.0833 10.2501H10.75ZM14.8333 10.1509V13.8493L13.5675 12.5834H11.9167V11.4168H13.5675L14.8333 10.1509ZM18.625 12.0001C18.625 10.9676 18.03 10.0809 17.1667 9.64928V14.3451C18.03 13.9193 18.625 13.0326 18.625 12.0001ZM17.1667 6.88428V8.08594C18.8525 8.58761 20.0833 10.1509 20.0833 12.0001C20.0833 13.8493 18.8525 15.4126 17.1667 15.9143V17.1159C19.5058 16.5851 21.25 14.4968 21.25 12.0001C21.25 9.50344 19.5058 7.41511 17.1667 6.88428V6.88428Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="speaker-wave-icon-clip">
          <rect
            width="14"
            height="14"
            fill="white"
            transform="translate(9 5)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

function StopIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className="h-[14px] w-[14px] shrink-0"
    >
      <path
        fillRule="evenodd"
        d="M4.5 7.5a3 3 0 0 1 3-3h9a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3h-9a3 3 0 0 1-3-3v-9Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

const SCF_TOOL_NAME = "SubmitSeeClickFixIssue";
const MALFORMED_REQUIREMENT_ERROR =
  "This action cannot be accepted at this time";
const KNOWN_REQUIREMENT_TYPES = new Set([RECAPTCHA_TYPE]);

function argsToRows(
  args: Record<string, unknown> | undefined,
): { label: string; value: { kind: "text"; value: unknown } }[] {
  return Object.entries(args ?? {}).map(([label, value]) => ({
    label,
    value: { kind: "text" as const, value },
  }));
}

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  statusMessage?: string | null;
  primaryColor: string;
  conversationId: string | null;
  sourceUrl?: string;
  agentId: string;
  baseUrl: string;
  organizationId?: string;
  onRetryMessage?: (messageId: string) => void;
  pendingApproval?: (PendingApproval & { error?: string }) | null;
  onApprove?: (
    decision: "approved" | "denied",
    clientArtifacts?: Record<string, string>,
    toolResult?: string,
  ) => void;
  onSkip?: (toolApprovalId: string) => void;
  voiceEnabled?: boolean;
  onSpeakMessage?: (messageId: string, text: string) => void;
  playingMessageId?: string | null;
  playbackState?: PlaybackState;
  conversationUIState?: ConversationUIState;
}

export function MessageList({
  messages,
  isLoading,
  statusMessage,
  primaryColor,
  conversationId,
  sourceUrl,
  agentId,
  baseUrl,
  organizationId,
  onRetryMessage,
  pendingApproval,
  onApprove,
  onSkip,
  voiceEnabled,
  onSpeakMessage,
  playingMessageId,
  playbackState,
  conversationUIState,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, isLoading, pendingApproval]);

  const lastMessage = messages[messages.length - 1];
  const isStreamingWithContent =
    lastMessage?.role === "assistant" && lastMessage.content.trim().length > 0;
  const shouldShowStatus =
    isLoading && !isStreamingWithContent && !pendingApproval;

  const lastAssistantIndex = messages.reduce(
    (acc, msg, idx) => (msg.role === "assistant" ? idx : acc),
    -1,
  );

  const recaptchaRequirement = pendingApproval?.clientRequirements?.find(
    (r) => r.type === RECAPTCHA_TYPE,
  );
  const recaptchaSiteKey =
    recaptchaRequirement?.parameters[RECAPTCHA_PARAM_SITE_KEY];
  const recaptchaAction =
    recaptchaRequirement?.parameters[RECAPTCHA_PARAM_ACTION];
  const isMalformedRequirement =
    !!recaptchaRequirement && (!recaptchaSiteKey || !recaptchaAction);

  const isVoiceInteractionLocked =
    conversationUIState === "conversation-speaking" ||
    (conversationUIState === "conversation-processing" && isLoading);

  function renderApprovalCard() {
    if (!pendingApproval) return null;

    if (pendingApproval.toolName === COLLECT_IMAGES_TOOL_NAME) {
      return (
        <ImageCollectionCard
          pendingApproval={pendingApproval}
          agentId={agentId}
          conversationId={pendingApproval.conversationId}
          baseUrl={baseUrl}
          onApprove={onApprove}
          isLoading={isLoading}
          primaryColor={primaryColor}
        />
      );
    }

    if (isMalformedRequirement) {
      return (
        <ToolApprovalCard
          rows={argsToRows(pendingApproval.args)}
          onApprove={onApprove}
          isLoading={isLoading}
          primaryColor={primaryColor}
          error={MALFORMED_REQUIREMENT_ERROR}
        />
      );
    }

    if (
      pendingApproval.toolName === SCF_TOOL_NAME &&
      recaptchaSiteKey &&
      recaptchaAction
    ) {
      return (
        <ScfToolApprovalCard
          pendingApproval={pendingApproval}
          onApprove={onApprove}
          onSkip={
            onSkip ? () => onSkip(pendingApproval.toolApprovalId) : undefined
          }
          isLoading={isLoading}
          primaryColor={primaryColor}
          siteKey={recaptchaSiteKey}
          action={recaptchaAction}
          error={pendingApproval.error}
          onLinkClick={(url, linkText) => {
            const attrs: Record<string, string> = {
              url,
              linkText,
              conversationId: pendingApproval.conversationId,
              sourceUrl: sourceUrl || "unknown",
              agentId,
            };
            if (organizationId) {
              attrs.organizationId = organizationId;
            }
            trackWidgetEvent("link.clicked", attrs);
          }}
        />
      );
    }

    if (import.meta.env.DEV) {
      const unknownTypes = (pendingApproval.clientRequirements ?? [])
        .map((r) => r.type)
        .filter((type) => !KNOWN_REQUIREMENT_TYPES.has(type));
      if (unknownTypes.length > 0) {
        console.warn(
          `[MessageList] Unhandled clientRequirements type(s): ${unknownTypes.join(", ")}`,
        );
      }
    }

    return (
      <ToolApprovalCard
        rows={argsToRows(pendingApproval.args)}
        onApprove={onApprove}
        onSkip={
          onSkip ? () => onSkip(pendingApproval.toolApprovalId) : undefined
        }
        isLoading={isLoading}
        primaryColor={primaryColor}
        error={pendingApproval.error}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div
        ref={containerRef}
        className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5"
        role="log"
        aria-label="Chat messages"
      >
        {messages.map((message, index) => {
          const isUser = message.role === "user";
          const hasContent = message.content.trim().length > 0;

          if (!hasContent && !isUser) {
            return null;
          }

          const isStreamingMessage =
            isLoading && index === lastAssistantIndex && !isUser;
          const showSpeakerButton =
            voiceEnabled &&
            !isUser &&
            !message.error &&
            hasContent &&
            !isStreamingMessage;
          const isThisMessagePlaying =
            playingMessageId === message.id &&
            (playbackState === "playing" || playbackState === "loading");

          return (
            <div
              key={message.id}
              role="article"
              aria-label={`Message from ${message.role}`}
              className={`flex ${isUser ? "justify-end" : "flex-col items-start"}`}
            >
              {isUser ? (
                <div className="max-w-[85%] rounded-3xl bg-[#E0E0E0] px-6 py-4 text-[#232323]">
                  <p className="whitespace-pre-wrap wrap-break-word text-base leading-[1.21]">
                    {message.content}
                  </p>
                </div>
              ) : message.error ? (
                <ErrorMessage
                  content={message.content}
                  retryable={message.retryable ?? false}
                  onRetry={
                    message.retryable && onRetryMessage
                      ? () => onRetryMessage(message.id)
                      : undefined
                  }
                  primaryColor={primaryColor}
                />
              ) : (
                <div className="w-full">
                  <MarkdownMessage
                    content={message.content}
                    onLinkClick={(url, linkText) => {
                      const linkClickedAttributes: Record<string, string> = {
                        url,
                        linkText,
                        messageId: message.id,
                        sourceUrl: sourceUrl || "unknown",
                        agentId,
                      };
                      if (conversationId) {
                        linkClickedAttributes.conversationId = conversationId;
                      }
                      if (organizationId) {
                        linkClickedAttributes.organizationId = organizationId;
                      }
                      trackWidgetEvent("link.clicked", linkClickedAttributes);
                    }}
                  />
                  {showSpeakerButton && (
                    <div className="mt-1 flex items-center gap-2">
                      {isThisMessagePlaying ? (
                        <button
                          type="button"
                          onClick={() =>
                            onSpeakMessage?.(message.id, message.content)
                          }
                          aria-label="Stop playback"
                          title="Stop playback"
                          disabled={false}
                          className="flex h-6 w-6 min-h-6 min-w-6 items-center justify-center rounded text-black/80 transition-colors hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <StopIcon />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            onSpeakMessage?.(message.id, message.content)
                          }
                          aria-label="Read this message aloud"
                          title="Read this message aloud"
                          disabled={isVoiceInteractionLocked}
                          className="flex h-6 w-6 min-h-6 min-w-6 items-center justify-center rounded text-black/80 transition-colors hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <SpeakerWaveIcon />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {renderApprovalCard()}
        {shouldShowStatus && (
          <div className="py-4" aria-hidden="true">
            <StatusMessage
              message={statusMessage}
              primaryColor={primaryColor}
            />
          </div>
        )}
      </div>
      <div role="status" className="sr-only">
        {shouldShowStatus && statusMessage}
      </div>
    </div>
  );
}
